import "./Queue.css";
import { Button } from "./Button";
import { EmptyState } from "./Card";
import { Chip } from "./Status";
import type { Issue } from "../lib/tasks";
import { tx } from "../i18n";

/* ══ 일감 › 대기열(t-in-port-work-queue) ══
   나루가 다음에 집을 이슈 순서. **순서도 판정도 서버가 낸다** — `GET /dispatch/queue` 가 마일스톤 → 큐 순서 →
   등록순(`TASK_ORDER`)으로 주고, 줄마다 집을 트랙(`track`) 또는 못 집는 사유 코드(`skip`)를 싣는다. 화면은 그대로
   그리고 코드를 문장으로만 바꾼다(모르는 코드는 그대로). 옮기기는 이웃 두 id 만 보낸다(`POST /tasks/:id/queue-order`). */

export type QueueTask = Issue & { repositoryIds?: string[] };
export type QueueItem = { task: QueueTask; track: string | null; skip: string | null; runAttempts?: number; needs?: string[] };

const SKIPS = ["has-children", "session-open", "held", "deps", "stalled", "no-track", "needs-tag"] as const;
type SkipCode = (typeof SKIPS)[number];
const isSkip = (s: string): s is SkipCode => (SKIPS as readonly string[]).includes(s);
const TRACKS = ["plan", "design", "run", "review", "merge"] as const;
const trackText = (t: string) => ((TRACKS as readonly string[]).includes(t) ? tx(`chat.track.${t as (typeof TRACKS)[number]}`) : t);

export const skipText = (s: string) => (isSkip(s) ? tx(`queue.skip.${s}`) : s);
export const skipWhy = (s: string) => (isSkip(s) ? tx(`queue.why.${s}`) : s);

/** 「n일 기다림」 — 자동 실행을 켠 때부터. 그 시각이 없으면 모름이다(0일로 적지 않는다). */
export function waitDays(since: string | null | undefined, now = Date.now()): number | null {
  if (!since) return null;
  const t = Date.parse(since);
  return Number.isNaN(t) ? null : Math.max(0, Math.floor((now - t) / 86_400_000));
}

/** 옮길 때 보낼 이웃 — 줄에서 그 자리의 앞뒤 id. 값(순위)은 서버가 정한다. */
export function neighbors(ids: string[], from: number, to: number): { prevId?: string; nextId?: string } {
  const rest = ids.filter((_, i) => i !== from);
  return { prevId: to > 0 ? rest[to - 1] : undefined, nextId: to < rest.length ? rest[to] : undefined };
}

export function QueueTable({
  rows,
  projectName,
  why,
  busy,
  onMove,
}: {
  rows: QueueItem[];
  projectName: (id: string | null | undefined) => string;
  why: boolean;
  busy: boolean;
  onMove: (from: number, to: number) => void;
}) {
  if (!rows.length) return <EmptyState title={tx("queue.empty")} desc={tx("queue.emptyDesc")} />;
  const firstTake = rows.findIndex((r) => r.track !== null);
  return (
    <div className="b-table-wrap">
      <table className="b-table">
        <thead>
          <tr>
            <th className="is-end">#</th>
            <th>{tx("ops.col.issue")}</th>
            <th>{tx("hist.col.project")}</th>
            <th>{tx("queue.col.repos")}</th>
            <th className="is-end">{tx("queue.col.wait")}</th>
            <th>{tx("queue.col.state")}</th>
            <th className="is-end">{tx("queue.col.move")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const t = r.task;
            const days = waitDays(t.agentRequestedAt);
            const repos = t.repositoryIds ?? [];
            return (
              <tr key={t.id}>
                <td className="is-end tnum">{i + 1}</td>
                <td className="is-primary queue-title">
                  <a href={`#/board?open=${encodeURIComponent(t.id)}`}>{t.title}</a> <code className="muted">{t.id}</code>
                  {why && r.skip ? (
                    <p className="caption queue-why">
                      {skipWhy(r.skip)}
                      {r.skip === "needs-tag" && r.needs?.length ? ` (${r.needs.join(", ")})` : ""}
                    </p>
                  ) : null}
                </td>
                <td>{t.projectId ? projectName(t.projectId) : <span className="muted">{tx("hist.projectNone")}</span>}</td>
                <td>
                  {repos.length ? (
                    <>
                      <code>{repos[0]}</code>
                      {repos.length > 1 ? <span className="muted"> +{repos.length - 1}</span> : null}
                    </>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td className="is-end">{days === null ? tx("ops.unknown") : tx("queue.days", { n: days })}</td>
                <td>
                  {r.track ? (
                    <Chip tone={i === firstTake ? "success" : "info"}>
                      {i === firstTake ? "● " : "◔ "}
                      {tx(i === firstTake ? "queue.next" : "queue.take", { track: trackText(r.track) })}
                    </Chip>
                  ) : (
                    <Chip tone="muted">○ {skipText(r.skip ?? "")}</Chip>
                  )}
                </td>
                <td className="is-end">
                  <div className="queue-move">
                    <Button size="sm" variant="ghost" disabled={busy || i === 0} onClick={() => onMove(i, 0)}>
                      {tx("queue.top")}
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy || i === 0} aria-label={tx("queue.upLabel", { title: t.title })} onClick={() => onMove(i, i - 1)}>
                      ↑
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy || i === rows.length - 1} aria-label={tx("queue.downLabel", { title: t.title })} onClick={() => onMove(i, i + 1)}>
                      ↓
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
